"use client";

import React from "react";
import { useTranslations } from "next-intl";
import { TriangleAlert } from "lucide-react";
import NumericText from "@/components/NumericText";
import { Skeleton } from "@/components/ui/skeleton";
import { useGetAttemptViolationsTotalQuery } from "@/api/assignment/assignmentApi";
import { cn } from "@/src/lib/utils";

interface ViolationsCellProps {
  assignmentId: number;
  studentId: number;
  attemptId: number | null;
  compact?: boolean;
}

/**
 * Violations counter for a single row of the students table.
 *
 * The list endpoint does not carry the number, so every row asks the actions
 * endpoint for it on its own. RTK Query dedupes and caches per
 * (assignment, student, attempt), so re-renders and page revisits are free.
 */
export const ViolationsCell: React.FC<ViolationsCellProps> = ({
  assignmentId,
  studentId,
  attemptId,
  compact = false,
}) => {
  const t = useTranslations();

  const { data, isLoading, isError } = useGetAttemptViolationsTotalQuery(
    { assignmentId, studentId, attemptId: attemptId ?? 0 },
    { skip: !assignmentId || !studentId || !attemptId }
  );

  /* no attempt yet — nothing to count */
  if (!attemptId) {
    return null;
  }

  if (isLoading) {
    return <Skeleton className={cn("h-6 rounded-sm", compact ? "w-8" : "w-10")} />;
  }

  if (isError || typeof data !== "number") {
    return null;
  }

  const isClean = data === 0;

  return (
    <span
      title={t("label-assignment-violations")}
      aria-label={t("label-assignment-violations")}
      className={cn(
        "inline-flex h-6 items-center gap-1 rounded-sm border px-1.5 text-xs font-medium whitespace-nowrap",
        isClean
          ? "border-[#E5E5E5] text-muted-foreground"
          : "border-red-200 bg-red-50 text-red-600"
      )}
    >
      <TriangleAlert className="size-3.5" />
      <NumericText>{data}</NumericText>
    </span>
  );
};
